import { REQUEST_TIMEOUT_MS, UPLOAD_ANSWER_MS, UPLOAD_STALL_MS } from '@constants/timing';
import { ERRORS, STATUS_ERRORS } from '@locales/id/errors';
import { ERROR_CODE, type ErrorCode } from './errors';

/** Same origin as the dashboard (the server stack's Caddy routes /api to the backend). */
export const API_BASE = '/api';

/**
 * One reason of a failure: the field it is about (`items[1].epc`; null for the request as a whole), a sentence safe
 * to show, and for some a code (e.g. why a load was refused).
 */
export interface ErrorDetail { field: string | null; message: string; code?: string }

/**
 * An API failure. `message` is safe to show (the backend's Indonesian text, or a generic one); `code` is what a
 * caller branches on (never the text); `details` holds one entry per broken field of a validation failure.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ErrorCode | string,
    readonly details: readonly ErrorDetail[] = [],
    readonly requestId?: string,
    /** a 429's Retry-After (seconds), when the server sent one */
    readonly retryAfterS?: number,
    /** ids the failure names (`{ orderId }`: the open order a counter exchange ran into); never personal data */
    readonly detail?: Record<string, unknown>,
  ) {
    super(message);
  }
}

/** `{ success: false, error }`: every failed answer of the backend. */
interface Failure { success: false; error: { code?: unknown; message?: unknown; details?: unknown; detail?: unknown; requestId?: unknown } }
/** `{ success: true, data, meta? }`: every successful JSON answer of the backend. */
interface Success { success: true; message?: string; data: unknown; meta?: unknown }

const isDetail = (d: unknown): d is { field?: unknown; message: string; code?: unknown } => !!d && typeof d === 'object' && typeof (d as { message?: unknown }).message === 'string';
const isFailure = (b: unknown): b is Failure => !!b && typeof b === 'object' && (b as Failure).success === false && typeof (b as Failure).error === 'object';
const isSuccess = (b: unknown): b is Success => !!b && typeof b === 'object' && (b as Success).success === true && 'data' in (b as object);

/** The generic code of a status, for answers that did not come from the backend (a proxy, the network). */
const STATUS_CODE: Record<number, ErrorCode> = {
  400: ERROR_CODE.BAD_REQUEST,
  401: ERROR_CODE.UNAUTHORIZED,
  403: ERROR_CODE.FORBIDDEN,
  404: ERROR_CODE.NOT_FOUND,
  409: ERROR_CODE.CONFLICT,
  413: ERROR_CODE.PAYLOAD_TOO_LARGE,
  429: ERROR_CODE.TOO_MANY_REQUESTS,
  503: ERROR_CODE.SERVICE_UNAVAILABLE,
};

/** Turn a failed answer into an ApiError: the backend's envelope when there is one, else a generic text per status. */
function failure(status: number, body: unknown): ApiError {
  if (isFailure(body)) {
    const e = body.error;
    const message = typeof e.message === 'string' && e.message ? e.message : (STATUS_ERRORS[status] ?? ERRORS.server);
    const details = Array.isArray(e.details) ? e.details.filter(isDetail).map((d) => ({ field: typeof d.field === 'string' ? d.field : null, message: d.message, ...(typeof d.code === 'string' ? { code: d.code } : {}) })) : [];
    const code = typeof e.code === 'string' ? e.code : (STATUS_CODE[status] ?? ERROR_CODE.INTERNAL_ERROR);
    const detail = e.detail && typeof e.detail === 'object' && !Array.isArray(e.detail) ? (e.detail as Record<string, unknown>) : undefined;
    return new ApiError(message, status, code, details, typeof e.requestId === 'string' ? e.requestId : undefined, undefined, detail);
  }
  // not the backend speaking (a proxy page, a gateway timeout): never show its text
  const message = status >= 500 ? ERRORS.server : (STATUS_ERRORS[status] ?? ERRORS.unprocessable);
  return new ApiError(message, status, STATUS_CODE[status] ?? ERROR_CODE.INTERNAL_ERROR);
}

const offline = () => new ApiError(ERRORS.offline, 0, ERROR_CODE.NETWORK);

/** Readable message for any thrown value (catch blocks). */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return ERRORS.unexpected;
}

export const LOGIN_PATH = '/auth/login';

/** Fired when any non-auth request gets 401: the app drops back to the login screen. */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

/**
 * A wrong sign-in is a 400, so a 401 anywhere else means the session ended (expired, signed out elsewhere). The sign-in
 * steps themselves (/auth/login, /auth/login/verify) have no session to lose: their 401 is theirs to explain.
 */
const noticeExpired = (status: number, path: string) => {
  if (status === 401 && path !== LOGIN_PATH && !path.startsWith(`${LOGIN_PATH}/`)) window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

/**
 * A signal that aborts after `ms`, or when `outer` aborts (AbortSignal.any / .timeout are missing in older Safari, so
 * built by hand). `timedOut()` tells the two apart; `done()` releases the timer.
 */
function deadline(ms: number, outer?: AbortSignal) {
  const ctl = new AbortController();
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    ctl.abort();
  }, ms);
  const onOuter = () => ctl.abort();
  if (outer?.aborted) ctl.abort();
  else outer?.addEventListener('abort', onOuter, { once: true });
  return {
    signal: ctl.signal,
    timedOut: () => timedOut,
    done: () => {
      window.clearTimeout(timer);
      outer?.removeEventListener('abort', onOuter);
    },
  };
}

/**
 * The one place the envelope is unwrapped: a plain answer resolves to its `data`, a list (an answer with `meta`)
 * to `{ items, meta }` (`OffsetPage` / `CursorPage`); 204 resolves to undefined. Failures throw ApiError. A request
 * without a whole answer after REQUEST_TIMEOUT_MS (a hung mobile connection) fails as NETWORK, so the callers' retry
 * paths apply; the caller's own `signal` stops it as ABORTED.
 */
export async function api<T>(path: string, opts: { method?: string; body?: unknown; headers?: Record<string, string>; signal?: AbortSignal } = {}): Promise<T> {
  const d = deadline(REQUEST_TIMEOUT_MS, opts.signal);
  try {
    let res: Response;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        method: opts.method ?? (opts.body !== undefined ? 'POST' : 'GET'),
        headers: opts.body !== undefined || opts.headers ? { ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}), ...opts.headers } : undefined,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
        credentials: 'same-origin',
        signal: d.signal,
      });
    } catch {
      throw opts.signal?.aborted && !d.timedOut() ? new ApiError(ERRORS.aborted, 0, ERROR_CODE.ABORTED) : offline();
    }
    noticeExpired(res.status, path);
    if (res.status === 204) return undefined as T;
    // revalidated GETs (ETag): with the default cache mode the browser answers a 304 from its cache as a 200, so a
    // 304 only reaches here if a caller set If-None-Match itself; it has no body, so it must not look like data
    if (res.status === 304) throw new ApiError(ERRORS.unprocessable, 304, ERROR_CODE.BAD_RESPONSE);
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // the body stopped half-way (the deadline, a dropped line): a lost connection, never "bad data"
      if (d.signal.aborted) throw offline();
    }
    return unwrap<T>(res.status, body);
  } finally {
    d.done();
  }
}

/** A JSON answer that arrived: its `data` (`{ items, meta }` for a list), or the ApiError it stands for. */
function unwrap<T>(status: number, body: unknown): T {
  if (status < 200 || status >= 300) throw failure(status, body);
  if (!isSuccess(body)) throw new ApiError(ERRORS.unprocessable, status, ERROR_CODE.BAD_RESPONSE);
  return (body.meta !== undefined ? { items: body.data, meta: body.meta } : body.data) as T;
}

export interface UploadOpts {
  /** bytes sent so far of the total (the request body only; the answer follows once it is all sent) */
  onProgress?: (sent: number, total: number) => void;
  /** stops the upload: the promise then rejects with ERROR_CODE.ABORTED */
  signal?: AbortSignal;
  /** extra request headers (an anti-bot token, an upload session) */
  headers?: Record<string, string>;
  /** reads the answer's headers once it has arrived (an upload session the server hands out) */
  onHeaders?: (get: (name: string) => string | null) => void;
}

/**
 * POST a raw body (an image) with its own content type, reporting upload progress (XMLHttpRequest: fetch cannot
 * report it). The answer is unwrapped like `api()` and failures throw the same ApiError (envelope or status text).
 * Bytes that stop moving for UPLOAD_STALL_MS, or no answer UPLOAD_ANSWER_MS after the last byte, count as a lost
 * connection (status 0), so the caller can offer a retry instead of waiting forever.
 */
export function upload<T>(path: string, body: Blob, opts: UploadOpts = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const aborted = () => new ApiError(ERRORS.aborted, 0, ERROR_CODE.ABORTED);
    if (opts.signal?.aborted) {
      reject(aborted());
      return;
    }
    const xhr = new XMLHttpRequest();
    let stalled = false;
    let timer = 0;
    const watch = (ms: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        stalled = true;
        xhr.abort();
      }, ms);
    };
    const onAbort = () => xhr.abort();
    const done = () => {
      window.clearTimeout(timer);
      opts.signal?.removeEventListener('abort', onAbort);
    };
    xhr.open('POST', `${API_BASE}${path}`);
    xhr.setRequestHeader('content-type', body.type);
    for (const [k, v] of Object.entries(opts.headers ?? {})) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      watch(UPLOAD_STALL_MS);
      if (e.lengthComputable) opts.onProgress?.(e.loaded, e.total);
    };
    xhr.upload.onload = () => watch(UPLOAD_ANSWER_MS);
    xhr.onerror = () => {
      done();
      reject(offline());
    };
    xhr.onabort = () => {
      done();
      reject(stalled ? offline() : aborted());
    };
    xhr.onload = () => {
      done();
      noticeExpired(xhr.status, path);
      if (xhr.status === 204) {
        resolve(undefined as T);
        return;
      }
      opts.onHeaders?.((name) => xhr.getResponseHeader(name));
      let parsed: unknown = null;
      try {
        parsed = JSON.parse(xhr.responseText);
      } catch {
        // not JSON (a proxy's 413 page): failure() falls back to the status text
      }
      try {
        resolve(unwrap<T>(xhr.status, parsed));
      } catch (e) {
        const wait = Number(xhr.getResponseHeader('retry-after'));
        // a 429 says how long to wait: the queue's next try honours it
        reject(e instanceof ApiError && xhr.status === 429 && wait > 0 ? new ApiError(e.message, e.status, e.code, e.details, e.requestId, wait, e.detail) : e);
      }
    };
    opts.signal?.addEventListener('abort', onAbort, { once: true });
    watch(UPLOAD_STALL_MS);
    xhr.send(body);
  });
}

/** A file the server sends as an attachment (CSV export): its content and the name it suggests. With `body`, a JSON
 * POST (filters that must not travel in a URL). */
export async function fetchFile(path: string, body?: unknown): Promise<{ blob: Blob; filename: string | null }> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, body === undefined ? { credentials: 'same-origin' } : { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  } catch {
    throw offline();
  }
  noticeExpired(res.status, path);
  if (!res.ok) throw failure(res.status, await res.json().catch(() => null));
  let blob: Blob;
  try {
    blob = await res.blob();
  } catch {
    // the server broke the download half-way (it never ends a partial file as if it were complete)
    throw offline();
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? null;
  return { blob, filename: name };
}

/** Mutation with a JSON body (the backend rejects non-JSON mutations as CSRF protection). */
export const send = <T>(path: string, body: unknown = {}, method = 'POST') => api<T>(path, { method, body });

/** DELETE, also with a JSON body for the same reason. */
export const remove = <T = void>(path: string) => api<T>(path, { method: 'DELETE', body: {} });
